import {
  AdditionalEquipmentCode,
  AdditionalEquipmentState,
  AxleStructureEnum,
  VehicleDriverCabEnum,
  VehicleDriveEnum,
  VehicleRearSuspension,
  BOX_FLOOR_THICKNESS_SELECT_OPTIONS,
  BOX_FRONT_TYPE_SELECT_OPTIONS,
  BOX_PILLARS_SELECT_OPTIONS,
  BOX_REAR_RAIL_OPTIONS,
  BOX_REAR_WALL_SELECT_OPTIONS,
  BOX_ROOF_MATERIAL_OPTIONS,
  BOX_SIDE_DOORS_OPTIONS,
  BOX_SIDE_RAIL_OPTIONS,
  BOX_SUB_CHASSIS_SELECT_OPTIONS,
  CentreOfGravityFormValues,
  ChasisLoad,
  ChassisLoadsState,
  VehicleMeasureCode,
  VehicleMeasuresState,
  WorkOrderVehicleDetails,
  QuotationFormValues,
} from "../../types";
import {
  AddOnCardConfig,
  AddOnSectionConfig,
  AddOnTableColumnKey,
  AddOnTableConfig,
  BoxDetailsFieldConfig,
  DiagramPageData,
  FormFieldConfig,
  QuotationFieldConfig,
  VehicleDetailsFieldConfig,
  WeightAndWallFieldConfig,
  LoadDistributionFormValues,
  LoadDistributionPdfData,
  LoadDistributionPdfTableRow,
  VehicleDetailsFormValues,
  BoxDetailsFormValues,
  WeightAndWallFormValues,
  WorkOrderPicture,
} from "./types";

export const MOCK_VEHICLE_DETAILS: Record<number, WorkOrderVehicleDetails> = {
  1: {
    workOrderNo: "13487",
    customerName: "A_LADANI",
    subCustomer: "A_LADANI",
    chassis: "9368474",
    manufacturer: "SCANIA",
    model: "P 420 B6x2*4NA",
    axleStructure: AxleStructureEnum.SIX_X2,
    year: "2017",
    boxType: "OPEN_BOX",
  },
  2: {
    workOrderNo: "13488",
    customerName: "SHLOMO_LOGISTICS",
    subCustomer: "SHLOMO_LOGISTICS",
    chassis: "8472631",
    manufacturer: "MERCEDES",
    model: "Actros 1845",
    axleStructure: AxleStructureEnum.EIGHT_X2,
    year: "2019",
    boxType: "OPEN_BOX",
  },
  3: {
    workOrderNo: "13489",
    customerName: "GAL_COLD_CHAIN",
    subCustomer: "GAL_COLD_CHAIN",
    chassis: "5629183",
    manufacturer: "VOLVO",
    model: "FH 460",
    axleStructure: AxleStructureEnum.TEN_X2,
    year: "2020",
    boxType: "OPEN_BOX",
  },
};

export const getMockVehicleDetails = (id: number): WorkOrderVehicleDetails =>
  MOCK_VEHICLE_DETAILS[id] ?? MOCK_VEHICLE_DETAILS[1];

export const VEHICLE_DETAILS_FORM_I18N_PREFIX = "WORK_ORDER.VEHICLE_DETAILS.FORM";
export const BOX_DETAILS_FORM_I18N_PREFIX = "WORK_ORDER.BOX_DETAILS.FORM";
export const WEIGHT_AND_WALL_FORM_I18N_PREFIX = "WORK_ORDER.WEIGHT_AND_WALL";
export const LOAD_DISTRIBUTION_FORM_I18N_PREFIX = "WORK_ORDER.LOAD_DISTRIBUTION";
export const ADD_ON_FORM_I18N_PREFIX = "WORK_ORDER.ADD_ON.FORM";

const addOnField = (suffix: string) => `${ADD_ON_FORM_I18N_PREFIX}.FIELDS.${suffix}`;
const addOnOption = (suffix: string) => `${ADD_ON_FORM_I18N_PREFIX}.OPTIONS.${suffix}`;
const addOnSection = (suffix: string) => `${ADD_ON_FORM_I18N_PREFIX}.SECTIONS.${suffix}`;
const addOnTable = (suffix: string) => `${ADD_ON_FORM_I18N_PREFIX}.TABLES.${suffix}`;
const addOnTableColumn = (suffix: string) =>
  `${ADD_ON_FORM_I18N_PREFIX}.TABLES.COLUMNS.${suffix}`;
const addOnValue = (suffix: string) => `${ADD_ON_FORM_I18N_PREFIX}.VALUES.${suffix}`;

const formField = (suffix: string) =>
  `${VEHICLE_DETAILS_FORM_I18N_PREFIX}.FIELDS.${suffix}`;

const boxFormField = (suffix: string) =>
  `${BOX_DETAILS_FORM_I18N_PREFIX}.FIELDS.${suffix}`;

const boxFormOption = (suffix: string) =>
  `${BOX_DETAILS_FORM_I18N_PREFIX}.OPTIONS.${suffix}`;

const formValue = (suffix: string) =>
  `${VEHICLE_DETAILS_FORM_I18N_PREFIX}.VALUES.${suffix}`;

const boxFormValue = (suffix: string) =>
  `${BOX_DETAILS_FORM_I18N_PREFIX}.VALUES.${suffix}`;

const weightAndWallField = (suffix: string) =>
  `${WEIGHT_AND_WALL_FORM_I18N_PREFIX}.FIELDS.${suffix}`;

const weightAndWallPlaceholder = (suffix: string) =>
  `${WEIGHT_AND_WALL_FORM_I18N_PREFIX}.PLACEHOLDERS.${suffix}`;

const weightAndWallOption = (suffix: string) =>
  `${WEIGHT_AND_WALL_FORM_I18N_PREFIX}.OPTIONS.${suffix}`;

const loadDistributionField = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.FIELDS.${suffix}`;

const loadDistributionSection = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.SECTIONS.${suffix}`;

const loadDistributionPlaceholder = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PLACEHOLDERS.${suffix}`;

const loadDistributionOption = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.OPTIONS.${suffix}`;

const loadDistributionSummary = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.SUMMARY.${suffix}`;

const loadDistributionDiagram = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.DIAGRAM.${suffix}`;

export const MOCK_VEHICLE_FORM_VALUES: VehicleDetailsFormValues = {
  manufacturer: "",
  year: "2026",
  model: "",
  licenseNo: "123-132-123",
  chassisNo: "12345671234567123",
  driverCab: VehicleDriverCabEnum.SINGLE,
  passengers: "10",
  drive: VehicleDriveEnum.FOUR_X2,
  tire: formValue("TIRE"),
  rearSuspension: VehicleRearSuspension.SPRING,
  wheelbase: "12345",
  frontAxle: "12345",
  rearAxle: "12345",
  frontOverhang: "1234",
  chassisWidth: "1234",
  chassisHeight: "1234",
  totalHeight: "1234",
  chassisWeight: "12345",
  netBodyWeight: "12345",
  licenseWeight: "12345",
  frontLoad: "12345",
  rearLoad: "12345",
  hydraulic: "",
  transferBody: false,
};

export const VEHICLE_DATA_FIELDS: VehicleDetailsFieldConfig[] = [
  {
    field: "manufacturer",
    labelKey: formField("MANUFACTURER"),
    type: "select",
  },
  { field: "year", labelKey: formField("YEAR") },
  {
    field: "model",
    labelKey: formField("MODEL"),
    type: "select",
  },
  { field: "licenseNo", labelKey: formField("LICENSE_NO") },
  { field: "chassisNo", labelKey: formField("CHASSIS_NO") },
];

export const VEHICLE_CONFIG_FIELDS: VehicleDetailsFieldConfig[] = [
  {
    field: "driverCab",
    labelKey: formField("DRIVER_CAB"),
    type: "select",
  },
  {
    field: "passengers",
    labelKey: formField("PASSENGERS"),
    type: "number",
    showSpinners: true,
  },
  {
    field: "drive",
    labelKey: formField("DRIVE"),
    type: "select",
  },
  { field: "tire", labelKey: formField("TIRE") },
  {
    field: "rearSuspension",
    labelKey: formField("REAR_SUSPENSION"),
    type: "select",
  },
];

export const VEHICLE_HYDRAULIC_FIELD: VehicleDetailsFieldConfig = {
  field: "hydraulic",
  labelKey: formField("HYDRAULIC"),
  type: "select",
};

export const CHASSIS_DATA_FIELDS: VehicleDetailsFieldConfig[] = [
  { field: "wheelbase", labelKey: formField("WHEELBASE") },
  { field: "frontAxle", labelKey: formField("FRONT_AXLE") },
  { field: "rearAxle", labelKey: formField("REAR_AXLE") },
  { field: "frontOverhang", labelKey: formField("FRONT_OVERHANG") },
  { field: "chassisWidth", labelKey: formField("CHASSIS_WIDTH") },
  { field: "chassisHeight", labelKey: formField("CHASSIS_HEIGHT") },
  { field: "totalHeight", labelKey: formField("TOTAL_HEIGHT") },
];

export const WEIGHT_DATA_FIELDS: VehicleDetailsFieldConfig[] = [
  { field: "chassisWeight", labelKey: formField("CHASSIS_WEIGHT") },
  { field: "netBodyWeight", labelKey: formField("NET_BODY_WEIGHT") },
  { field: "licenseWeight", labelKey: formField("LICENSE_WEIGHT") },
  { field: "frontLoad", labelKey: formField("FRONT_LOAD") },
  { field: "rearLoad", labelKey: formField("REAR_LOAD") },
];

export const MOCK_WEIGHT_AND_WALL_FORM_VALUES: WeightAndWallFormValues = {
  chassisWeight: "",
  netBox: "",
  frontWeighing: "",
  rearWeighing: "",
  generalWeighing: "",
  wallWeight: "",
  wallManufacturer: "",
  wallNumber: "",
};

export const WEIGHT_AND_WALL_FIELDS: WeightAndWallFieldConfig[] = [
  {
    field: "chassisWeight",
    labelKey: weightAndWallField("CHASSIS_WEIGHT"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "netBox",
    labelKey: weightAndWallField("NET_BOX"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "frontWeighing",
    labelKey: weightAndWallField("FRONT_WEIGHING"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "rearWeighing",
    labelKey: weightAndWallField("REAR_WEIGHING"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "generalWeighing",
    labelKey: weightAndWallField("GENERAL_WEIGHING"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "wallWeight",
    labelKey: weightAndWallField("WALL_WEIGHT"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
  {
    field: "wallManufacturer",
    labelKey: weightAndWallField("WALL_MANUFACTURER"),
    type: "select",
    placeholderKey: weightAndWallPlaceholder("SELECT"),
    options: [
      ["ALUM", weightAndWallOption("ALUMINUM")],
      ["HYDRAULIC", weightAndWallOption("HYDRAULIC")],
    ],
  },
  {
    field: "wallNumber",
    labelKey: weightAndWallField("WALL_NUMBER"),
    placeholderKey: weightAndWallPlaceholder("VALUE"),
  },
];

export const LOAD_DISTRIBUTION_SECTION_KEYS = {
  CALCULATION_DATA: loadDistributionSection("CALCULATION_DATA"),
  CHASSIS_WEIGHING: loadDistributionSection("CHASSIS_WEIGHING"),
  DRIVER_WALL_AUTO: loadDistributionSection("DRIVER_WALL_AUTO"),
} as const;

export const MOCK_LOAD_DISTRIBUTION_FORM_VALUES: LoadDistributionFormValues = {
  axleDistanceCalculation: "6330",
  centerOfGravityCalculation: "735",
  offsetCalculation: "-",
  rearAxle: "5420",
  generalSelf: "12570",
  frontAxle: "7,150",
  fuelLevel: "0.75",
  afterWeighingData: "FRONT_REAR_GENERAL",
  weighingComments: "",
  driverAndPassengers: "150",
  wallType: "DHLM-30",
  hydraulicWallNumber: "25018-25071",
};

export const LOAD_DISTRIBUTION_CALCULATION_FIELDS: FormFieldConfig<LoadDistributionFormValues>[] = [
  {
    field: "axleDistanceCalculation",
    labelKey: loadDistributionField("AXLE_DISTANCE_CALCULATION"),
    readOnly: true,
  },
  {
    field: "centerOfGravityCalculation",
    labelKey: loadDistributionField("CENTER_OF_GRAVITY_CALCULATION"),
    readOnly: true,
  },
  {
    field: "offsetCalculation",
    labelKey: loadDistributionField("OFFSET_CALCULATION"),
    readOnly: true,
  },
];

export const LOAD_DISTRIBUTION_WEIGHING_ROW1_FIELDS: FormFieldConfig<LoadDistributionFormValues>[] = [
  { field: "rearAxle", labelKey: loadDistributionField("REAR_AXLE") },
  { field: "generalSelf", labelKey: loadDistributionField("GENERAL_SELF") },
  { field: "frontAxle", labelKey: loadDistributionField("FRONT_AXLE") },
  { field: "fuelLevel", labelKey: loadDistributionField("FUEL_LEVEL") },
];

export const LOAD_DISTRIBUTION_AFTER_WEIGHING_FIELD: FormFieldConfig<LoadDistributionFormValues> = {
  field: "afterWeighingData",
  labelKey: loadDistributionField("AFTER_WEIGHING_DATA"),
  type: "select",
  options: [
    ["FRONT_REAR_GENERAL", loadDistributionOption("FRONT_REAR_GENERAL")],
  ],
};

export const LOAD_DISTRIBUTION_WEIGHING_COMMENTS_FIELD: FormFieldConfig<LoadDistributionFormValues> = {
  field: "weighingComments",
  labelKey: loadDistributionField("WEIGHING_COMMENTS"),
  placeholderKey: loadDistributionPlaceholder("COMMENTS"),
};

export const LOAD_DISTRIBUTION_DRIVER_WALL_FIELDS: FormFieldConfig<LoadDistributionFormValues>[] = [
  { field: "driverAndPassengers", labelKey: loadDistributionField("DRIVER_AND_PASSENGERS") },
  { field: "wallType", labelKey: loadDistributionField("WALL_TYPE") },
  { field: "hydraulicWallNumber", labelKey: loadDistributionField("HYDRAULIC_WALL_NUMBER") },
];

export type LoadDistributionSummaryTheme = "yellow" | "green" | "blue";

export type LoadDistributionSummaryCardConfig = {
  key: "rearAxle" | "frontAxle" | "general";
  titleKey: string;
  value: string;
  total: string;
  theme: LoadDistributionSummaryTheme;
};

export const LOAD_DISTRIBUTION_SUMMARY_CARDS: LoadDistributionSummaryCardConfig[] = [
  {
    key: "rearAxle",
    titleKey: loadDistributionSummary("REAR_AXLE"),
    value: "0",
    total: "7,500",
    theme: "yellow",
  },
  {
    key: "frontAxle",
    titleKey: loadDistributionSummary("FRONT_AXLE"),
    value: "0",
    total: "19,000",
    theme: "green",
  },
  {
    key: "general",
    titleKey: loadDistributionSummary("GENERAL"),
    value: "0",
    total: "26,000",
    theme: "blue",
  },
];

export const LOAD_DISTRIBUTION_DIAGRAM_TITLE = loadDistributionDiagram("TITLE");

export const LOAD_DISTRIBUTION_DIAGRAM_METRICS = loadDistributionDiagram("METRICS");

export const LOAD_DISTRIBUTION_RESULTS_TITLE = loadDistributionDiagram("RESULTS_TITLE");

export const LOAD_DISTRIBUTION_VEHICLE_OK_LABEL = loadDistributionDiagram("VEHICLE_OK");

export type LoadDistributionResultRow = {
  labelKey: string;
  value: string;
};

export const LOAD_DISTRIBUTION_RESULTS_ROWS: LoadDistributionResultRow[] = [
  { labelKey: loadDistributionDiagram("ROWS.FRONT_AXLE"), value: "0 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.REAR_AXLE"), value: "0 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.GENERAL"), value: "0 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.PERMITTED_FRONT"), value: "7,500 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.PERMITTED_REAR"), value: "19,000 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.PERMITTED_GENERAL"), value: "26,000 ק\"ג" },
  { labelKey: loadDistributionDiagram("ROWS.POSSIBLE_CARGO"), value: "0 ק\"ג" },
];

export const LOAD_DISTRIBUTION_RESULTS_FOOTER_ROWS: LoadDistributionResultRow[] = [
  { labelKey: loadDistributionDiagram("FOOTER.WB_60"), value: "3,870 מ\"מ" },
  { labelKey: loadDistributionDiagram("FOOTER.ADDITIONAL_FUEL"), value: "69 ק\"ג" },
];

const loadDistributionTable = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.TABLE.${suffix}`;

export const LOAD_DISTRIBUTION_TABLE_TITLE = loadDistributionTable("TITLE");

export const LOAD_DISTRIBUTION_TABLE_COLUMNS = {
  detail: loadDistributionTable("COLUMNS.DETAIL"),
  front: loadDistributionTable("COLUMNS.FRONT"),
  rear: loadDistributionTable("COLUMNS.REAR"),
  general: loadDistributionTable("COLUMNS.GENERAL"),
} as const;

export type LoadDistributionTableRow = {
  labelKey: string;
  front: string;
  rear: string;
  general: string;
  isStatus?: boolean;
};

export const LOAD_DISTRIBUTION_TABLE_ROWS: LoadDistributionTableRow[] = [
  {
    labelKey: loadDistributionTable("ROWS.SELF_EMPTY"),
    front: "5,420",
    rear: "7,150",
    general: "12,750",
  },
  {
    labelKey: loadDistributionTable("ROWS.BALANCE"),
    front: "0",
    rear: "0",
    general: "-",
  },
  {
    labelKey: loadDistributionTable("ROWS.DRIVER_PASSENGERS"),
    front: "150",
    rear: "0",
    general: "150",
  },
  {
    labelKey: loadDistributionTable("ROWS.CARGO"),
    front: "-",
    rear: "-",
    general: "-",
  },
  {
    labelKey: loadDistributionTable("ROWS.FUEL_ADDITION"),
    front: "23",
    rear: "46",
    general: "69",
  },
  {
    labelKey: loadDistributionTable("ROWS.TOTAL"),
    front: "5,323",
    rear: "8,166",
    general: "13,489",
  },
  {
    labelKey: loadDistributionTable("ROWS.TOTAL_PERMITTED"),
    front: "7,500",
    rear: "19,000",
    general: "26,000",
  },
  {
    labelKey: loadDistributionTable("ROWS.STATUS"),
    front: loadDistributionTable("STATUS_OK"),
    rear: loadDistributionTable("STATUS_OK"),
    general: loadDistributionTable("STATUS_OK"),
    isStatus: true,
  },
];

export const LOAD_DISTRIBUTION_TABLE_NOTE = loadDistributionTable("NOTE");

export type LoadDistributionTableMetric = {
  labelKey: string;
  value: string;
  unitKey?: string;
};

export const LOAD_DISTRIBUTION_TABLE_METRICS: LoadDistributionTableMetric[] = [
  { labelKey: loadDistributionTable("METRICS.WB_60"), value: "3,870", unitKey: loadDistributionTable("METRIC_UNITS.STEP") },
  { labelKey: loadDistributionTable("METRICS.COG"), value: "860", unitKey: loadDistributionTable("METRIC_UNITS.MM") },
  { labelKey: loadDistributionTable("METRICS.FUEL"), value: "69", unitKey: loadDistributionTable("METRIC_UNITS.KG") },
  { labelKey: loadDistributionTable("METRICS.HA"), value: "9779" },
  { labelKey: loadDistributionTable("METRICS.CHASSIS"), value: "299380" },
];

export const LOAD_DISTRIBUTION_TABLE_ACTIONS_TITLE = loadDistributionTable("ACTIONS_TITLE");

export const LOAD_DISTRIBUTION_TABLE_BUTTONS = {
  reset: loadDistributionTable("BUTTONS.RESET"),
  digitalSignature: loadDistributionTable("BUTTONS.DIGITAL_SIGNATURE"),
  sendEmail: loadDistributionTable("BUTTONS.SEND_EMAIL"),
  print: loadDistributionTable("BUTTONS.PRINT"),
} as const;

const loadDistributionPdf = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_TITLE = loadDistributionPdf("TITLE");

export const LOAD_DISTRIBUTION_PDF_FILE_NAME = loadDistributionPdf("FILE_NAME");

export const LOAD_DISTRIBUTION_PDF_SECTIONS = {
  PROJECT_INFO: "WORK_ORDER.DIAGRAM.SECTIONS.PROJECT_INFO",
  CUSTOMER_VEHICLE: "WORK_ORDER.DIAGRAM.SECTIONS.CUSTOMER_VEHICLE",
  TECHNICAL_SPEC: "WORK_ORDER.DIAGRAM.SECTIONS.TECHNICAL_SPEC",
} as const;

const loadDistributionPdfTable = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.TABLE.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_TABLE_COLUMNS = {
  detail: loadDistributionPdfTable("COLUMNS.DETAIL"),
  calculation: loadDistributionPdfTable("COLUMNS.CALCULATION"),
  measurement: loadDistributionPdfTable("COLUMNS.MEASUREMENT"),
} as const;

const loadDistributionPdfTableRows = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.TABLE.ROWS.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS = {
  AXLE_DISTANCE: loadDistributionPdfTableRows("AXLE_DISTANCE"),
  COG: loadDistributionPdfTableRows("COG"),
  OFFSET: loadDistributionPdfTableRows("OFFSET"),
} as const;

const loadDistributionPdfWeighing = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.WEIGHING.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_WEIGHING = {
  PERFORMED_LABEL: loadDistributionPdfWeighing("PERFORMED_LABEL"),
  PERFORMED_VALUE: loadDistributionPdfWeighing("PERFORMED_VALUE"),
  COMMENTS_LABEL: loadDistributionPdfWeighing("COMMENTS_LABEL"),
  COMMENTS_VALUE: loadDistributionPdfWeighing("COMMENTS_VALUE"),
} as const;

const loadDistributionPdfHydraulicWall = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.HYDRAULIC_WALL.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_HYDRAULIC_WALL = {
  TITLE: loadDistributionPdfHydraulicWall("TITLE"),
  COLUMNS: {
    WALL_NUMBER: loadDistributionPdfHydraulicWall("COLUMNS.WALL_NUMBER"),
    CHASSIS_NO: loadDistributionPdfHydraulicWall("COLUMNS.CHASSIS_NO"),
  },
} as const;

const loadDistributionPdfWeightDistribution = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.WEIGHT_DISTRIBUTION.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION = {
  TITLE: loadDistributionPdfWeightDistribution("TITLE"),
  TITLE_SUFFIX: loadDistributionPdfWeightDistribution("TITLE_SUFFIX"),
  COLUMNS: {
    DETAIL: loadDistributionPdfWeightDistribution("COLUMNS.DETAIL"),
    FRONT: loadDistributionPdfWeightDistribution("COLUMNS.FRONT"),
    REAR: loadDistributionPdfWeightDistribution("COLUMNS.REAR"),
    GENERAL: loadDistributionPdfWeightDistribution("COLUMNS.GENERAL"),
  },
} as const;

const loadDistributionPdfFooter = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.FOOTER.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_FOOTER_PARTS = {
  COMPANY: loadDistributionPdfFooter("COMPANY"),
  FOUNDED: loadDistributionPdfFooter("FOUNDED"),
  LEGAL: loadDistributionPdfFooter("LEGAL"),
  NUMBER: loadDistributionPdfFooter("NUMBER"),
  CITY: loadDistributionPdfFooter("CITY"),
} as const;

export type LoadDistributionPdfFooterSegment = {
  labelKey: string;
  ltr?: boolean;
};

export const LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_RTL: LoadDistributionPdfFooterSegment[] = [
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.CITY },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.NUMBER, ltr: true },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.LEGAL },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.FOUNDED, ltr: true },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.COMPANY },
];

export const LOAD_DISTRIBUTION_PDF_FOOTER_SEGMENTS_LTR: LoadDistributionPdfFooterSegment[] = [
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.COMPANY },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.FOUNDED, ltr: true },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.LEGAL },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.NUMBER, ltr: true },
  { labelKey: LOAD_DISTRIBUTION_PDF_FOOTER_PARTS.CITY },
];

const loadDistributionPdfManualWeighing = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.MANUAL_WEIGHING.${suffix}`;

export const LOAD_DISTRIBUTION_PDF_MANUAL_WEIGHING = {
  TITLE: loadDistributionPdfManualWeighing("TITLE"),
  FILLER_NAME_LABEL: loadDistributionPdfManualWeighing("FILLER_NAME_LABEL"),
  COLUMNS: {
    FRONT: loadDistributionPdfManualWeighing("COLUMNS.FRONT"),
    GENERAL: loadDistributionPdfManualWeighing("COLUMNS.GENERAL"),
    REAR: loadDistributionPdfManualWeighing("COLUMNS.REAR"),
  },
  ROWS: {
    KG: loadDistributionPdfManualWeighing("ROWS.KG"),
    WITH_WITHOUT_DRIVER: loadDistributionPdfManualWeighing("ROWS.WITH_WITHOUT_DRIVER"),
    EMPTY: loadDistributionPdfManualWeighing("ROWS.EMPTY"),
  },
} as const;

const loadDistributionPdfFuelInfo = (suffix: string) =>
  `${LOAD_DISTRIBUTION_FORM_I18N_PREFIX}.PDF.FUEL_INFO.${suffix}`;

const loadDistributionPdfFuelInfoField = (suffix: string) =>
  loadDistributionPdfFuelInfo(`FIELDS.${suffix}`);

const loadDistributionPdfFuelInfoValue = (suffix: string) =>
  loadDistributionPdfFuelInfo(`VALUES.${suffix}`);

export const LOAD_DISTRIBUTION_PDF_FUEL_INFO = {
  FIELDS: {
    FUEL_STATUS: loadDistributionPdfFuelInfoField("FUEL_STATUS"),
    TANK_SIZE: loadDistributionPdfFuelInfoField("TANK_SIZE"),
    TANK_LOCATION: loadDistributionPdfFuelInfoField("TANK_LOCATION"),
    RELATIVE_TO_AXLE: loadDistributionPdfFuelInfoField("RELATIVE_TO_AXLE"),
    STRUCTURE: loadDistributionPdfFuelInfoField("STRUCTURE"),
  },
  VALUES: {
    MARK_LINE_OR_WRITE: loadDistributionPdfFuelInfoValue("MARK_LINE_OR_WRITE"),
    LITER: loadDistributionPdfFuelInfoValue("LITER"),
    MM: loadDistributionPdfFuelInfoValue("MM"),
    STRUCTURE: loadDistributionPdfFuelInfoValue("STRUCTURE"),
  },
} as const;

export const MOCK_LOAD_DISTRIBUTION_PDF: LoadDistributionPdfData = {
  projectInfo: [
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.WORK_ORDER", value: "13497" },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.BOX_TYPE",
      value: "WORK_ORDER.DIAGRAM.VALUES.BOX_TYPE_OPEN_REAR_CRANE",
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.ISSUE_DATE",
      value: "17/02/2026",
    },
  ],
  customerVehicle: [
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.CUSTOMER",
      value: "WORK_ORDER.DIAGRAM.VALUES.CUSTOMER_MEIR",
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.MANUFACTURER_MODEL",
      value: "DAF - NGD FAD XF-XD",
    },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.YEAR", value: "2026" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REGISTRATION_NO", value: "2095" },
  ],
  technicalSpec: [
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.CHASSIS",
      value: loadDistributionPdf("VALUES.NOT_AVAILABLE"),
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REGISTRATION",
      value: loadDistributionPdf("VALUES.NOT_AVAILABLE"),
    },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.DRIVER_CAB", value: "1" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.WHEELBASE", value: "5370" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.DRIVE", value: "8x4" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.LICENSE_WEIGHT", value: "32,000" },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REAR_SUSPENSION",
      value: "WORK_ORDER.DIAGRAM.VALUES.SPRING",
    },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.TIRE", value: "315/80" },
  ],
  calculationTableRows: [
    {
      labelKey: LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.AXLE_DISTANCE,
      calculation: "6330",
      measurement: "6480",
    },
    {
      labelKey: LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.COG,
      calculation: "735",
      measurement: "885",
    },
    {
      labelKey: LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.OFFSET,
      calculation: "-",
      measurement: "150",
    },
  ],
  weighingNotes: [
    {
      labelKey: LOAD_DISTRIBUTION_PDF_WEIGHING.PERFORMED_LABEL,
      valueKey: LOAD_DISTRIBUTION_PDF_WEIGHING.PERFORMED_VALUE,
    },
    {
      labelKey: LOAD_DISTRIBUTION_PDF_WEIGHING.COMMENTS_LABEL,
      valueKey: LOAD_DISTRIBUTION_PDF_WEIGHING.COMMENTS_VALUE,
    },
  ],
  hydraulicWall: {
    titleKey: LOAD_DISTRIBUTION_PDF_HYDRAULIC_WALL.TITLE,
    columns: {
      wallNumberKey: LOAD_DISTRIBUTION_PDF_HYDRAULIC_WALL.COLUMNS.WALL_NUMBER,
      chassisNoKey: LOAD_DISTRIBUTION_PDF_HYDRAULIC_WALL.COLUMNS.CHASSIS_NO,
    },
    rows: Array.from({ length: 6 }, () => ({
      wallNumber: loadDistributionPdf("VALUES.NOT_AVAILABLE"),
      chassisNo: loadDistributionPdf("VALUES.NOT_AVAILABLE"),
    })),
  },
  weightDistribution: {
    titleKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.TITLE,
    titleSuffixKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.TITLE_SUFFIX,
    columns: {
      detailKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.COLUMNS.DETAIL,
      frontKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.COLUMNS.FRONT,
      rearKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.COLUMNS.REAR,
      generalKey: LOAD_DISTRIBUTION_PDF_WEIGHT_DISTRIBUTION.COLUMNS.GENERAL,
    },
    rows: [
      {
        labelKey: loadDistributionTable("ROWS.SELF_EMPTY"),
        front: "4444",
        rear: "6667",
        general: "11111",
      },
      {
        labelKey: loadDistributionTable("ROWS.BALANCE"),
        front: "0",
        rear: "0",
        general: "-",
      },
      {
        labelKey: loadDistributionTable("ROWS.DRIVER_PASSENGERS"),
        front: "225",
        rear: "0",
        general: "225",
      },
      {
        labelKey: loadDistributionTable("ROWS.CARGO"),
        front: "1703",
        rear: "12961",
        general: "14664",
      },
      {
        labelKey: loadDistributionTable("ROWS.TOTAL"),
        front: "6372",
        rear: "19628",
        general: "26000",
      },
      {
        labelKey: loadDistributionTable("ROWS.TOTAL_PERMITTED"),
        front: "7500",
        rear: "19000",
        general: "26000",
      },
    ],
  },
  manualWeighingForm: {
    rows: [
      { valueKey: LOAD_DISTRIBUTION_PDF_MANUAL_WEIGHING.ROWS.KG },
      { valueKey: LOAD_DISTRIBUTION_PDF_MANUAL_WEIGHING.ROWS.WITH_WITHOUT_DRIVER },
      { valueKey: LOAD_DISTRIBUTION_PDF_MANUAL_WEIGHING.ROWS.EMPTY },
    ],
  },
  fuelInfo: {
    fields: [
      {
        labelKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.FIELDS.FUEL_STATUS,
        valueKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.VALUES.MARK_LINE_OR_WRITE,
      },
      {
        labelKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.FIELDS.TANK_SIZE,
        valueKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.VALUES.LITER,
      },
      {
        labelKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.FIELDS.TANK_LOCATION,
        valueKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.VALUES.MM,
      },
      {
        labelKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.FIELDS.RELATIVE_TO_AXLE,
        valueKey: loadDistributionPdf("VALUES.NOT_AVAILABLE"),
      },
      {
        labelKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.FIELDS.STRUCTURE,
        valueKey: LOAD_DISTRIBUTION_PDF_FUEL_INFO.VALUES.STRUCTURE,
        ltr: true,
      },
    ],
  },
};

export const buildLoadDistributionPdfData = (
  form: LoadDistributionFormValues,
  base: LoadDistributionPdfData = MOCK_LOAD_DISTRIBUTION_PDF
): LoadDistributionPdfData => ({
  ...base,
  calculationTableRows: base.calculationTableRows.map((row) => {
    switch (row.labelKey) {
      case LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.AXLE_DISTANCE:
        return { ...row, calculation: form.axleDistanceCalculation };
      case LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.COG:
        return { ...row, calculation: form.centerOfGravityCalculation };
      case LOAD_DISTRIBUTION_PDF_TABLE_ROWS_KEYS.OFFSET:
        return { ...row, calculation: form.offsetCalculation };
      default:
        return row;
    }
  }),
  weighingNotes: [
    {
      labelKey: LOAD_DISTRIBUTION_PDF_WEIGHING.PERFORMED_LABEL,
      valueKey: LOAD_DISTRIBUTION_PDF_WEIGHING.PERFORMED_VALUE,
    },
    {
      labelKey: LOAD_DISTRIBUTION_PDF_WEIGHING.COMMENTS_LABEL,
      value: form.weighingComments.trim() || "-",
    },
  ],
  weightDistribution: {
    ...base.weightDistribution,
    rows: LOAD_DISTRIBUTION_TABLE_ROWS.filter((row) => !row.isStatus).map((row) => ({
      labelKey: row.labelKey,
      front: row.front,
      rear: row.rear,
      general: row.general,
    })),
  },
});

export const MOCK_LOAD_DISTRIBUTION_PDF_DATA: DiagramPageData = {
  projectInfo: [...MOCK_LOAD_DISTRIBUTION_PDF.projectInfo],
  customerVehicle: [...MOCK_LOAD_DISTRIBUTION_PDF.customerVehicle],
  technicalSpec: [...MOCK_LOAD_DISTRIBUTION_PDF.technicalSpec],
};

export const LOAD_DISTRIBUTION_PDF_TABLE_ROWS: LoadDistributionPdfTableRow[] = [
  ...MOCK_LOAD_DISTRIBUTION_PDF.calculationTableRows,
];

export const MOCK_BOX_FORM_VALUES: BoxDetailsFormValues = {
  recommended: "12000",
  minimum: "12000",
  frontSpacing: "12000",
  minLength: "12000",
  maxLength: "12000",
  generalLength: "12000",
  boxLength: "12000",
  centerOfGravity: "1234",
  centerOfGravityTo: "1234",
  centerOfGravityRange: "1234",
  maxRearOverhang: "1234",
  rearOverhangPlus: "1234",
  rearOverhang: "1234",
  floorSideHeight: "140",
  floorType: "SLG_STR_700E",
  floor: "THICKNESS_2",
  maxWidth: "1234",
  boxWidth: "1234",
  floorHeight: "1234",
  crossbeams: boxFormValue("CROSSBEAMS"),
  subChassis: "LSECT_70_70_190",
  rearWallType: "",
  rearWall: "SHEET_PIPES_CRANE",
  frontHeight: "1234",
  frontType: "SHEET_PIPES_LONG",
  sideDoorsQuantity: "99",
  sideDoors: "TWO_PROFILES_60_40_325",
  pillarsQuantity: "99",
  pillars: "EXTERNAL_2F_MID_QTY_2F",
  comment: "",
  clampingLocks: true,
  rearDoor: "TWO_PROFILES_60_40_325",
  sideRail: "BOTTOM_HYDRAULIC_ROOF",
  roofBeam: boxFormValue("ROOF_BEAM"),
  roofMaterial: "INSULATED_100MM_FIBER",
  panelInternal: "1234",
  maximum: "1234",
  sideLightOpening: "1234",
  rearLightOpening: "1234",
  hinge: boxFormValue("HINGE"),
  rearRail: "RAISED_EXTERNAL",
};

export const BOX_SPACING_FIELDS: BoxDetailsFieldConfig[] = [
  { field: "frontSpacing", labelKey: boxFormField("FRONT_SPACING") },
  { field: "minimum", labelKey: boxFormField("MINIMUM") },
  { field: "recommended", labelKey: boxFormField("RECOMMENDED") },
];

export const BOX_LENGTH_FIELDS: BoxDetailsFieldConfig[] = [
  { field: "boxLength", labelKey: boxFormField("BOX_LENGTH"), type: "number", showSpinners: true },
  { field: "generalLength", labelKey: boxFormField("GENERAL_LENGTH") },
  { field: "maxLength", labelKey: boxFormField("MAX_LENGTH"), readOnly: true },
  { field: "minLength", labelKey: boxFormField("MIN_LENGTH"), readOnly: true },
];

export const BOX_COG_MAIN_FIELD: BoxDetailsFieldConfig = {
  field: "centerOfGravity",
  labelKey: boxFormField("CENTER_OF_GRAVITY"),
};

export const BOX_COG_RANGE_FROM_FIELD: BoxDetailsFieldConfig = {
  field: "centerOfGravityRange",
  labelKey: boxFormField("CENTER_OF_GRAVITY_RANGE"),
};

export const BOX_COG_RANGE_TO_FIELD: BoxDetailsFieldConfig = {
  field: "centerOfGravityTo",
  labelKey: boxFormField("TO"),
};

export const BOX_OVERHANG_MAX_FIELD: BoxDetailsFieldConfig = {
  field: "maxRearOverhang",
  labelKey: boxFormField("MAX_REAR_OVERHANG"),
};

export const BOX_OVERHANG_PLUS_FIELD: BoxDetailsFieldConfig = {
  field: "rearOverhangPlus",
  labelKey: boxFormField("REAR_OVERHANG_PLUS"),
};

export const BOX_OVERHANG_FIELD: BoxDetailsFieldConfig = {
  field: "rearOverhang",
  labelKey: boxFormField("REAR_OVERHANG"),
};

export const BOX_WIDTH_FIELDS: BoxDetailsFieldConfig[] = [
  { field: "boxWidth", labelKey: boxFormField("BOX_WIDTH") },
  { field: "maxWidth", labelKey: boxFormField("MAX_WIDTH") },
  {
    field: "floor",
    labelKey: boxFormField("FLOOR"),
    type: "select",
    options: BOX_FLOOR_THICKNESS_SELECT_OPTIONS,
  },
  {
    field: "floorType",
    labelKey: boxFormField("FLOOR_TYPE"),
    type: "select",
    options: [["SLG_STR_700E", boxFormOption("FLOOR_TYPE")]],
  },
  {
    field: "floorSideHeight",
    labelKey: boxFormField("FLOOR_SIDE_HEIGHT"),
    type: "number",
    showSpinners: true,
  },
];

export const BOX_FLOOR_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "subChassis",
    labelKey: boxFormField("SUB_CHASSIS"),
    type: "select",
    options: BOX_SUB_CHASSIS_SELECT_OPTIONS,
  },
  { field: "crossbeams", labelKey: boxFormField("CROSSBEAMS") },
  { field: "floorHeight", labelKey: boxFormField("FLOOR_HEIGHT") },
];

export const BOX_STRUCTURE_TYPE_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "frontType",
    labelKey: boxFormField("FRONT_TYPE"),
    type: "select",
    options: BOX_FRONT_TYPE_SELECT_OPTIONS,
  },
  { field: "frontHeight", labelKey: boxFormField("FRONT_HEIGHT") },
  {
    field: "rearWall",
    labelKey: boxFormField("REAR_WALL"),
    type: "select",
    options: BOX_REAR_WALL_SELECT_OPTIONS,
  },
];

export const BOX_REAR_WALL_TYPE_FIELD: BoxDetailsFieldConfig = {
  field: "rearWallType",
  labelKey: boxFormField("REAR_WALL_TYPE"),
};

export const BOX_STRUCTURE_DOOR_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "pillars",
    labelKey: boxFormField("PILLARS"),
    type: "select",
    options: BOX_PILLARS_SELECT_OPTIONS,
  },
  {
    field: "pillarsQuantity",
    labelKey: boxFormField("QUANTITY"),
    type: "number",
    showSpinners: true,
    narrow: true,
  },
  {
    field: "sideDoors",
    labelKey: boxFormField("SIDE_DOORS"),
    type: "select",
    options: BOX_SIDE_DOORS_OPTIONS,
  },
  {
    field: "sideDoorsQuantity",
    labelKey: boxFormField("QUANTITY"),
    type: "number",
    showSpinners: true,
    narrow: true,
  },
];

export const BOX_ROOF_TYPE_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "roofMaterial",
    labelKey: boxFormField("ROOF_MATERIAL"),
    type: "select",
    options: BOX_ROOF_MATERIAL_OPTIONS,
  },
  { field: "roofBeam", labelKey: boxFormField("ROOF_BEAM") },
  {
    field: "sideRail",
    labelKey: boxFormField("SIDE_RAIL"),
    type: "select",
    options: BOX_SIDE_RAIL_OPTIONS,
  },
];

export const BOX_ROOF_OPENING_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "rearRail",
    labelKey: boxFormField("REAR_RAIL"),
    type: "select",
    options: BOX_REAR_RAIL_OPTIONS,
  },
  { field: "hinge", labelKey: boxFormField("HINGE") },
  { field: "rearLightOpening", labelKey: boxFormField("REAR_LIGHT_OPENING") },
  { field: "sideLightOpening", labelKey: boxFormField("SIDE_LIGHT_OPENING") },
  { field: "maximum", labelKey: boxFormField("MAXIMUM") },
  { field: "panelInternal", labelKey: boxFormField("PANEL_INTERNAL") },
];

export const BOX_CURTAIN_ROOF_OPENING_FIELDS: BoxDetailsFieldConfig[] = [
  {
    field: "rearRail",
    labelKey: boxFormField("REAR_RAIL"),
    type: "select",
    options: BOX_REAR_RAIL_OPTIONS,
  },
  { field: "hinge", labelKey: boxFormField("HINGE") },
  { field: "rearLightOpening", labelKey: boxFormField("LIGHT_OPENING") },
  { field: "maximum", labelKey: boxFormField("MAXIMUM") },
  { field: "panelInternal", labelKey: boxFormField("PANEL_INTERNAL") },
];

export const DIAGRAM_DEPARTMENT_TABS = [
  { id: "DIAGRAM", labelKey: "WORK_ORDER.DIAGRAM.DEPARTMENT_TABS.DIAGRAM" },
  { id: "BENDING", labelKey: "WORK_ORDER.DIAGRAM.DEPARTMENT_TABS.BENDING" },
  { id: "CUTTING", labelKey: "WORK_ORDER.DIAGRAM.DEPARTMENT_TABS.CUTTING" },
  {
    id: "FRONT_REAR_WALL",
    labelKey: "WORK_ORDER.DIAGRAM.DEPARTMENT_TABS.FRONT_REAR_WALL",
  },
  { id: "ROOF", labelKey: "WORK_ORDER.DIAGRAM.DEPARTMENT_TABS.ROOF" },
] as const;

export const DIAGRAM_PAGE_COUNT = 4;

const BASE_DIAGRAM_PAGE: DiagramPageData = {
  layout: "overview",
  projectInfo: [
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.WORK_ORDER", value: "13497" },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.BOX_TYPE",
      value: "WORK_ORDER.DIAGRAM.VALUES.BOX_TYPE_OPEN_REAR_CRANE",
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.ISSUE_DATE",
      value: "17/02/2026",
    },
  ],
  customerVehicle: [
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.CUSTOMER",
      value: "WORK_ORDER.DIAGRAM.VALUES.CUSTOMER_MEIR",
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.MANUFACTURER_MODEL",
      value: "DAF - NGD FAD XF-XD",
    },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.YEAR", value: "2026" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REGISTRATION_NO", value: "2095" },
  ],
  technicalSpec: [
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.CHASSIS", value: "6500" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REGISTRATION", value: "6200" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.DRIVER_CAB", value: "1550" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.WHEELBASE", value: "5370" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.DRIVE", value: "8x4" },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.LICENSE_WEIGHT", value: "32,000" },
    {
      labelKey: "WORK_ORDER.DIAGRAM.FIELDS.REAR_SUSPENSION",
      value: "WORK_ORDER.DIAGRAM.VALUES.SPRING",
    },
    { labelKey: "WORK_ORDER.DIAGRAM.FIELDS.TIRE", value: "315/80" },
  ],
};

const DIAGRAM_PAGE_2: DiagramPageData = {
  layout: "sections",
  sections: [
    {
      titleKey: "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.TITLE",
      layout: "default",
      fields: [
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.FLOOR_LENGTH",
          value: "6520",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.FRONT_WALL",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.DIMENSIONS_FLOOR.FRONT_WALL",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.BOX_LENGTH",
          value: "6500",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.PILLARS",
          value: "-",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.BOX_WIDTH",
          value: "2500",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.FASTENING_LOCKS",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.DIMENSIONS_FLOOR.FASTENING_LOCKS",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.FLOOR_THICKNESS",
          value: "6",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.REAR_WALL",
          value: "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.DIMENSIONS_FLOOR.REAR_WALL",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.DIMENSIONS_FLOOR.FIELDS.FLOOR_TYPE",
          value: "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.DIMENSIONS_FLOOR.FLOOR_TYPE",
        },
      ],
    },
    {
      titleKey: "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.TITLE",
      layout: "singleColumn",
      fields: [
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.FRONT_CABLE_TENSIONERS",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.FRONT_CABLE_TENSIONERS",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.STRAP_TENSIONERS",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.STRAP_TENSIONERS",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.SIDE_CABLE_TENSIONERS",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.SIDE_CABLE_TENSIONERS",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.OPEN_BASKET",
          value: "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.OPEN_BASKET",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.PILLAR_BASKET",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.PILLAR_BASKET",
        },
        {
          labelKey:
            "WORK_ORDER.DIAGRAM.PAGE_2.SECTIONS.STRAPS_TENSIONERS.FIELDS.WATER_JERRYCAN",
          value:
            "WORK_ORDER.DIAGRAM.PAGE_2.VALUES.STRAPS_TENSIONERS.WATER_JERRYCAN",
        },
      ],
    },
  ],
};

const DIAGRAM_PAGE_3: DiagramPageData = {
  layout: "toolsAndNotes",
  toolboxesAndAccessories: [
    {
      labelKey: "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.TOOLBOX",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.TOOLBOXES_ACCESSORIES.TOOLBOX",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.REAR_LIGHTS",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.TOOLBOXES_ACCESSORIES.REAR_LIGHTS",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.SIDE_CABLE_TENSIONERS",
      value:
        "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.TOOLBOXES_ACCESSORIES.SIDE_CABLE_TENSIONERS",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.REAR_GUARD",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.NOT_AVAILABLE",
    },
    {
      labelKey: "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.DOORS",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.TOOLBOXES_ACCESSORIES.DOORS",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.SIDE_GUARD",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.NOT_AVAILABLE",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.MUDGUARD",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.NOT_AVAILABLE",
    },
    {
      labelKey:
        "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.TOOLBOXES_ACCESSORIES.FIELDS.SIDE_LIGHTING",
      value: "WORK_ORDER.DIAGRAM.PAGE_3.VALUES.NOT_AVAILABLE",
    },
  ],
  customNotes: {
    titleKey: "WORK_ORDER.DIAGRAM.PAGE_3.SECTIONS.CUSTOM_NOTES.TITLE",
    notes: [
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.CLIMBING_LADDERS",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.CABLE_TILT_RING",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.PALLET_JACK_FIXTURE",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.FUEL_DRAIN_PLUG_COVER",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.DIESEL_LOCK",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.BATTERY_LOCK",
      "WORK_ORDER.DIAGRAM.PAGE_3.NOTES.LOAD_SPREADER",
    ],
  },
};

const DIAGRAM_PAGE_4: DiagramPageData = {
  layout: "singleSection",
  section: {
    titleKey: "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.TITLE",
    layout: "singleColumn",
    fields: [
      {
        labelKey: "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.FRONT",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.LIGHTING_ELECTRONICS.FRONT",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.INCLUDES",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.NOT_AVAILABLE",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.EXTERNAL_BUTTON_NEAR_WALL",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.NOT_AVAILABLE",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.MICROPHONE",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.NOT_AVAILABLE",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.REVERSE_PROJECTORS",
        value:
          "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.LIGHTING_ELECTRONICS.REVERSE_PROJECTORS",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.SERIAL",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.LIGHTING_ELECTRONICS.SERIAL",
      },
      {
        labelKey:
          "WORK_ORDER.DIAGRAM.PAGE_4.SECTIONS.LIGHTING_ELECTRONICS.FIELDS.CABIN_WIRE_CONNECTION",
        value: "WORK_ORDER.DIAGRAM.PAGE_4.VALUES.NOT_AVAILABLE",
      },
    ],
  },
};

export const MOCK_DIAGRAM_PAGES: DiagramPageData[] = [
  { ...BASE_DIAGRAM_PAGE },
  DIAGRAM_PAGE_2,
  DIAGRAM_PAGE_3,
  DIAGRAM_PAGE_4,
];

const createAddOnItem = (
  enabled: boolean,
  selectedOption = "",
  note = "",
  quantity = "",
  size = "",
  location = "",
  itemType = "",
  optionEnabled = false
) => ({
  enabled,
  selectedOption,
  note,
  quantity,
  size,
  location,
  itemType,
  optionEnabled,
});

export const MOCK_ADD_ON_FORM_VALUES = {
  sideGuard: createAddOnItem(true, "IRON"),
  mudguard: createAddOnItem(true, "REGULAR"),
  sideLighting: createAddOnItem(false),
  interiorLighting: createAddOnItem(true),
  rearLights: createAddOnItem(true),
  heightLighting: createAddOnItem(true),
  reverseProjector: createAddOnItem(true),
  pipeMounts: createAddOnItem(true, "ABS"),
  microphoneLights: createAddOnItem(true, "SHORT"),
  camera: createAddOnItem(true),
  frontCableTensioner: createAddOnItem(
    true,
    "",
    addOnValue("FRONT_CABLE_TENSIONER_NOTE"),
    "99"
  ),
  sideCableTensioner: createAddOnItem(
    true,
    "",
    addOnValue("SIDE_CABLE_TENSIONER_NOTE"),
    "99"
  ),
  strapTensioners: createAddOnItem(
    true,
    "WIDE",
    addOnValue("STRAP_TENSIONERS_NOTE"),
    "99"
  ),
  apcRings: createAddOnItem(true, "", addOnValue("APC_RINGS_NOTE"), "99"),
  recessedRings: createAddOnItem(true, "", "", "99"),
  loadSpreaderDevice: createAddOnItem(
    true,
    "",
    addOnValue("LOAD_SPREADER_DEVICE_NOTE"),
    "99",
    "1000"
  ),
  waterJerrycan: {
    ...createAddOnItem(true, "", "", "2"),
    location: addOnValue("WATER_JERRYCAN_LOCATION"),
    itemType: addOnValue("WATER_JERRYCAN_TYPE"),
  },
  polesBasket: createAddOnItem(
    true,
    "",
    addOnValue("POLES_BASKET_NOTE"),
    "2"
  ),
  palletJackMount: createAddOnItem(true, "", addOnValue("PALLET_JACK_MOUNT_NOTE")),
  spareMount: createAddOnItem(true, "BASKET", addOnValue("SPARE_MOUNT_NOTE")),
  rearLightProtection: {
    ...createAddOnItem(true, "", addOnValue("REAR_LIGHT_PROTECTION_NOTE")),
    optionEnabled: true,
  },
  rearBumper: createAddOnItem(true, "", addOnValue("REAR_BUMPER_NOTE")),
  frontCraneWings: createAddOnItem(true, "TIN"),
  hazardousMaterials: createAddOnItem(true, "", addOnValue("HAZARDOUS_MATERIALS_NOTE")),
  longTransportTriangle: createAddOnItem(true),
  reflectors: createAddOnItem(true, "REAR"),
  dieselLock: createAddOnItem(true),
  batteriesLock: createAddOnItem(true),
  ureaLock: createAddOnItem(true),
  spareWheelLock: createAddOnItem(true, "", addOnValue("SPARE_WHEEL_LOCK_NOTE")),
  hydraulicTailLiftLock: createAddOnItem(true, "EYELET"),
  hydraulicTailLiftMotorLock: createAddOnItem(true),
  towHook: createAddOnItem(true),
};

const addOnCard = (
  field: AddOnCardConfig["field"],
  titleSuffix: string,
  config: Partial<Omit<AddOnCardConfig, "field" | "titleKey">> = {}
): AddOnCardConfig => ({
  field,
  titleKey: addOnField(titleSuffix),
  layout: "vertical",
  fields: ["note"],
  ...config,
});

export const PART_95_AND_ELECTRICITY_SECTIONS: AddOnSectionConfig[] = [
  {
    titleKey: addOnSection("PART_95"),
    rows: [
      [
        addOnCard("sideGuard", "SIDE_GUARD", {
          fields: ["radio", "note"],
          radioLabelKey: addOnField("CHOOSE"),
          radioOptions: [
            { value: "IRON", labelKey: addOnOption("IRON") },
            { value: "ALUMINUM", labelKey: addOnOption("ALUMINUM") },
          ],
        }),
        addOnCard("mudguard", "MUDGUARD", {
          fields: ["radio", "note"],
          radioLabelKey: addOnField("CHOOSE"),
          radioOptions: [
            { value: "REGULAR", labelKey: addOnOption("REGULAR") },
            { value: "WINGS", labelKey: addOnOption("WINGS") },
          ],
        }),
        addOnCard("sideLighting", "SIDE_LIGHTING"),
      ],
    ],
  },
  {
    titleKey: addOnSection("ELECTRICITY"),
    rows: [
      [
        addOnCard("interiorLighting", "INTERIOR_LIGHTING"),
        addOnCard("rearLights", "REAR_LIGHTS"),
      ],
      [
        addOnCard("heightLighting", "HEIGHT_LIGHTING"),
        addOnCard("reverseProjector", "REVERSE_PROJECTOR"),
      ],
      [
        addOnCard("pipeMounts", "PIPE_MOUNTS", {
          fields: ["radio"],
          radioLabelKey: addOnField("PARAMETER"),
          radioOptions: [
            { value: "ABS", labelKey: addOnOption("ABS") },
            { value: "ELECTRICITY", labelKey: addOnOption("ELECTRICITY") },
            { value: "AIR", labelKey: addOnOption("AIR") },
          ],
        }),
        addOnCard("microphoneLights", "MICROPHONE_LIGHTS", {
          fields: ["radio"],
          radioLabelKey: addOnField("CHOOSE"),
          radioOptions: [
            { value: "SHORT", labelKey: addOnOption("SHORT") },
            { value: "LONG", labelKey: addOnOption("LONG") },
          ],
        }),
        addOnCard("camera", "CAMERA"),
      ],
    ],
  },
];

export const TYING_AND_LOADING_SECTION: AddOnSectionConfig = {
  titleKey: addOnSection("TYING_AND_LOADING"),
  rows: [
    [
      addOnCard("frontCableTensioner", "FRONT_CABLE_TENSIONER", {
        layout: "horizontal",
        fields: ["note", "quantity"],
      }),
      addOnCard("sideCableTensioner", "SIDE_CABLE_TENSIONER", {
        layout: "horizontal",
        fields: ["note", "quantity"],
      }),
    ],
    [
      addOnCard("strapTensioners", "STRAP_TENSIONERS", {
        layout: "horizontal",
        fields: ["radio", "quantity", "note"],
        radioLabelKey: addOnField("MODEL_SELECTION"),
        radioOptions: [
          { value: "WIDE", labelKey: addOnOption("WIDE") },
          { value: "NARROW", labelKey: addOnOption("NARROW") },
        ],
      }),
      addOnCard("apcRings", "APC_RINGS", {
        layout: "horizontal",
        fields: ["note", "quantity"],
      }),
    ],
    [
      addOnCard("recessedRings", "RECESSED_RINGS", {
        layout: "horizontal",
        fields: ["quantity", "note"],
      }),
      addOnCard("loadSpreaderDevice", "LOAD_SPREADER_DEVICE", {
        layout: "horizontal",
        fields: ["size", "quantity", "note"],
      }),
    ],
  ],
};

export const STORAGE_AND_AUXILIARY_SECTION: AddOnSectionConfig = {
  titleKey: addOnSection("STORAGE_AND_AUXILIARY"),
  rows: [
    [
      addOnCard("waterJerrycan", "WATER_JERRYCAN", {
        layout: "horizontal",
        fields: ["location", "itemType", "quantity"],
      }),
      addOnCard("polesBasket", "POLES_BASKET", {
        layout: "horizontal",
        fields: ["note", "quantity"],
      }),
    ],
    [
      addOnCard("palletJackMount", "PALLET_JACK_MOUNT", {
        layout: "vertical",
        fields: ["note"],
      }),
      addOnCard("spareMount", "SPARE_MOUNT", {
        layout: "horizontal",
        fields: ["note", "radio"],
        radioLabelKey: addOnField("CHOOSE"),
        radioOptions: [
          { value: "BASKET", labelKey: addOnOption("BASKET") },
          { value: "REGULAR", labelKey: addOnOption("REGULAR") },
        ],
      }),
    ],
  ],
};

export const SAFETY_AND_STANDARDS_SECTION: AddOnSectionConfig = {
  titleKey: addOnSection("SAFETY_AND_STANDARDS"),
  rows: [
    [
      addOnCard("rearLightProtection", "REAR_LIGHT_PROTECTION", {
        fields: ["note", "subCheckbox"],
        subCheckboxLabelKey: addOnField("REAR_LIGHT_PROTECTION"),
      }),
      addOnCard("rearBumper", "REAR_BUMPER"),
      addOnCard("frontCraneWings", "FRONT_CRANE_WINGS", {
        fields: ["radio", "note"],
        radioLabelKey: addOnField("ITEM_TYPE"),
        radioModalTriggerOptions: ["TIN", "PLASTIC"],
        radioOptions: [
          { value: "TIN", labelKey: addOnOption("TIN") },
          { value: "PLASTIC", labelKey: addOnOption("PLASTIC") },
        ],
      }),
    ],
    [
      addOnCard("hazardousMaterials", "HAZARDOUS_MATERIALS"),
      addOnCard("longTransportTriangle", "LONG_TRANSPORT_TRIANGLE", {
        notePlaceholderKey: addOnField("VALUE"),
      }),
      addOnCard("reflectors", "REFLECTORS", {
        fields: ["radio"],
        radioLabelKey: addOnField("REFLECTORS"),
        radioOptions: [
          { value: "REAR", labelKey: addOnOption("REAR_REFLECTORS") },
          { value: "SIDE", labelKey: addOnOption("SIDE_REFLECTOR") },
        ],
      }),
    ],
  ],
};

export const LOCKS_SECTION: AddOnSectionConfig = {
  titleKey: addOnSection("LOCKS"),
  rows: [
    [
      addOnCard("dieselLock", "DIESEL_LOCK", {
        notePlaceholderKey: addOnField("VALUE"),
      }),
      addOnCard("batteriesLock", "BATTERIES_LOCK", {
        notePlaceholderKey: addOnField("VALUE"),
      }),
      addOnCard("ureaLock", "UREA_LOCK", {
        notePlaceholderKey: addOnField("VALUE"),
      }),
    ],
    [
      addOnCard("spareWheelLock", "SPARE_WHEEL_LOCK"),
      addOnCard("hydraulicTailLiftLock", "HYDRAULIC_TAIL_LIFT_LOCK", {
        fields: ["radio"],
        radioOptions: [
          { value: "EYELET", labelKey: addOnOption("EYELET") },
          { value: "HASP", labelKey: addOnOption("HASP") },
        ],
      }),
      addOnCard("hydraulicTailLiftMotorLock", "HYDRAULIC_TAIL_LIFT_MOTOR_LOCK"),
    ],
  ],
};

export const VEHICLE_ADD_ONS_SECTION: AddOnSectionConfig = {
  titleKey: addOnSection("VEHICLE_ADD_ONS"),
  rows: [
    [
      addOnCard("towHook", "TOW_HOOK", {
        notePlaceholderKey: addOnField("VALUE"),
      }),
    ],
  ],
};

export const EQUIPMENT_TABLE_COLUMNS: AddOnTableColumnKey[] = [
  "location",
  "itemType",
  "quantity",
  "note",
];

export const HEIGHT_NOTE_TABLE_COLUMNS: AddOnTableColumnKey[] = ["height", "note"];

export const ADD_ON_TABLE_COLUMN_LABELS: Record<AddOnTableColumnKey, string> = {
  location: addOnTableColumn("LOCATION"),
  itemType: addOnTableColumn("TYPE"),
  quantity: addOnTableColumn("QUANTITY"),
  note: addOnTableColumn("NOTE"),
  height: addOnTableColumn("HEIGHT"),
};

export const MOCK_ADD_ON_TABLES = {
  toolboxes: [
    {
      id: "toolbox-1",
      location: addOnValue("TABLE_LOCATION_BETWEEN_WHEELS"),
      itemType: addOnValue("TABLE_TYPE_WATERPROOF"),
      quantity: "99",
      note: addOnValue("TABLE_NOTE_DIMENSIONS"),
      height: "",
    },
  ],
  openBasket: [
    {
      id: "open-basket-1",
      location: addOnValue("TABLE_LOCATION_BETWEEN_WHEELS"),
      itemType: addOnValue("TABLE_TYPE_ABOVE_SPARE"),
      quantity: "99",
      note: addOnValue("TABLE_NOTE_DIMENSIONS"),
      height: "",
    },
  ],
  tyingStrip: [
    {
      id: "tying-strip-1",
      location: "",
      itemType: "",
      quantity: "",
      height: "1234",
      note: addOnValue("TYING_STRIP_NOTE"),
    },
  ],
};

export const EQUIPMENT_ADD_ON_TABLES: AddOnTableConfig[] = [
  {
    tableKey: "toolboxes",
    titleKey: addOnTable("TOOLBOXES"),
    modalTitleKey: addOnTable("ADD_TOOLBOXES"),
    columns: EQUIPMENT_TABLE_COLUMNS,
  },
  {
    tableKey: "openBasket",
    titleKey: addOnTable("OPEN_BASKET"),
    modalTitleKey: addOnTable("ADD_OPEN_BASKET"),
    columns: EQUIPMENT_TABLE_COLUMNS,
  },
];

export const TYING_STRIP_ADD_ON_TABLES: AddOnTableConfig[] = [
  {
    tableKey: "tyingStrip",
    titleKey: addOnTable("TYING_STRIP"),
    modalTitleKey: addOnTable("ADD_TYING_STRIP"),
    columns: HEIGHT_NOTE_TABLE_COLUMNS,
  },
];

export const ADD_ON_TABLES: AddOnTableConfig[] = [
  ...EQUIPMENT_ADD_ON_TABLES,
  ...TYING_STRIP_ADD_ON_TABLES,
];

export const PICTURES_I18N_PREFIX = "WORK_ORDER.PICTURES";

const pictureValue = (suffix: string) => `${PICTURES_I18N_PREFIX}.${suffix}`;

export const MOCK_WORK_ORDER_PICTURES: WorkOrderPicture[] = [
  {
    id: "picture-1",
    fileNameKey: pictureValue("MOCK_FILE_NAME"),
    dateKey: pictureValue("MOCK_DATE"),
    uploaderInitials: "EP",
  },
  {
    id: "picture-2",
    fileNameKey: pictureValue("MOCK_FILE_NAME"),
    dateKey: pictureValue("MOCK_DATE"),
    uploaderInitials: "EP",
  },
];

export const CENTRE_OF_GRAVITY_I18N_PREFIX = "WORK_ORDER.CENTRE_OF_GRAVITY";

const cogField = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.FIELDS.${suffix}`;
const cogButton = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.BUTTONS.${suffix}`;
const cogOption = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.OPTIONS.${suffix}`;
const cogRow = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.ROWS.${suffix}`;
const chassisLoads = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.CHASSIS_LOADS.${suffix}`;
const chassisLoadColumn = (suffix: string) => chassisLoads(`COLUMNS.${suffix}`);
const chassisLoadDescription = (suffix: string) => chassisLoads(`DESCRIPTIONS.${suffix}`);
const vehicleMeasures = (suffix: string) =>
  `${CENTRE_OF_GRAVITY_I18N_PREFIX}.VEHICLE_MEASURES.${suffix}`;
const vehicleMeasureColumn = (suffix: string) => vehicleMeasures(`COLUMNS.${suffix}`);
const vehicleMeasureDescription = (suffix: string) => vehicleMeasures(`DESCRIPTIONS.${suffix}`);
const vehicleMeasureNote = (suffix: string) => vehicleMeasures(`NOTES.${suffix}`);
const additionalEquipment = (suffix: string) =>
  `${CENTRE_OF_GRAVITY_I18N_PREFIX}.ADDITIONAL_EQUIPMENT.${suffix}`;
const additionalEquipmentColumn = (suffix: string) => additionalEquipment(`COLUMNS.${suffix}`);
const additionalEquipmentDescription = (suffix: string) =>
  additionalEquipment(`DESCRIPTIONS.${suffix}`);
const finalResults = (suffix: string) => `${CENTRE_OF_GRAVITY_I18N_PREFIX}.FINAL_RESULTS.${suffix}`;
const finalResultsColumn = (suffix: string) => finalResults(`COLUMNS.${suffix}`);
const finalResultsParameter = (suffix: string) => finalResults(`PARAMETERS.${suffix}`);

export const MOCK_CENTRE_OF_GRAVITY_FORM: CentreOfGravityFormValues = {
  calculatedValue: "1234",
  minRange: "1234",
  maxRange: "1234",
  centerOfGravityStatus: "OK",
  currentLength: "12000",
  minimalLength: "12000",
  maximalLength: "12000",
  vehicleLengthStatus: "NOT_OK",
  adjustmentMode: "SET_MAX_LENGTH",
};

export const COG_ROW_KEYS = {
  CENTER_OF_GRAVITY: cogRow("CENTER_OF_GRAVITY"),
  VEHICLE_LENGTH: cogRow("VEHICLE_LENGTH"),
} as const;

export const COG_CENTER_METRICS = [
  { field: "calculatedValue" as const, labelKey: cogField("CALCULATED_VALUE") },
  { field: "minRange" as const, labelKey: cogField("MIN_RANGE") },
  { field: "maxRange" as const, labelKey: cogField("MAX_RANGE") },
];

export const COG_LENGTH_METRICS = [
  { field: "currentLength" as const, labelKey: cogField("CURRENT") },
  { field: "minimalLength" as const, labelKey: cogField("MINIMAL") },
  { field: "maximalLength" as const, labelKey: cogField("MAXIMAL") },
];

export const COG_ACTION_BUTTONS = [
  { key: "RANGE_VALUES", labelKey: cogButton("RANGE_VALUES") },
  { key: "ENTER_RANGE", labelKey: cogButton("ENTER_RANGE") },
] as const;

export const COG_ADJUSTMENT_OPTIONS = [
  { value: "SET_MAX_LENGTH" as const, labelKey: cogOption("SET_MAX_LENGTH") },
  { value: "SET_MIN_VALUE" as const, labelKey: cogOption("SET_MIN_VALUE") },
];

export const CHASSIS_LOADS_SECTION_TITLE = chassisLoads("SECTION_TITLE");

export const CHASSIS_LOADS_COLUMNS = {
  symbol: chassisLoadColumn("SYMBOL"),
  description: chassisLoadColumn("DESCRIPTION"),
  value: chassisLoadColumn("VALUE"),
  calculation: chassisLoadColumn("CALCULATION"),
} as const;

export type ChassisLoadConfig = {
  key: ChasisLoad;
  symbol: string;
  descriptionKey: string;
};

export const CHASSIS_LOAD_ROWS: ChassisLoadConfig[] = [
  { key: "pwo", symbol: "PWO", descriptionKey: chassisLoadDescription("PWO") },
  { key: "pwf", symbol: "PWF", descriptionKey: chassisLoadDescription("PWF") },
  { key: "pwr", symbol: "PWR", descriptionKey: chassisLoadDescription("PWR") },
  { key: "swo", symbol: "SWO", descriptionKey: chassisLoadDescription("SWO") },
  { key: "swf", symbol: "SWF", descriptionKey: chassisLoadDescription("SWF") },
  { key: "swr", symbol: "SWR", descriptionKey: chassisLoadDescription("SWR") },
];

export const MOCK_CHASSIS_LOADS: ChassisLoadsState = {
  pwo: { value: "26000", calculationValue: "26000" },
  pwf: { value: "8000", calculationValue: "8000" },
  pwr: { value: "19000", calculationValue: "19000" },
  swo: { value: "9438", calculationValue: "9438" },
  swf: { value: "5519", calculationValue: "5519" },
  swr: { value: "3919", calculationValue: "3919" },
};

export const VEHICLE_MEASURES_SECTION_TITLE = vehicleMeasures("SECTION_TITLE");
export const VEHICLE_MEASURES_COLUMNS = {
  symbol: vehicleMeasureColumn("SYMBOL"),
  description: vehicleMeasureColumn("DESCRIPTION"),
  value: vehicleMeasureColumn("VALUE"),
  calculation: vehicleMeasureColumn("CALCULATION"),
} as const;

export type VehicleMeasureConfig = {
  key: VehicleMeasureCode;
  symbol: string;
  descriptionKey: string;
  withAdjuster?: boolean;
  noteKey?: string;
};

export const VEHICLE_MEASURE_ROWS: VehicleMeasureConfig[] = [
  { key: "wb", symbol: "WB", descriptionKey: vehicleMeasureDescription("WB") },
  { key: "cg", symbol: "CG", descriptionKey: vehicleMeasureDescription("CG") },
  { key: "fo", symbol: "FO", descriptionKey: vehicleMeasureDescription("FO") },
  {
    key: "g",
    symbol: "G",
    descriptionKey: vehicleMeasureDescription("G"),
    withAdjuster: true,
    noteKey: vehicleMeasureNote("G"),
  },
  { key: "k", symbol: "K", descriptionKey: vehicleMeasureDescription("K") },
  { key: "rg", symbol: "RG", descriptionKey: vehicleMeasureDescription("RG") },
  { key: "lbox", symbol: "Lbox", descriptionKey: vehicleMeasureDescription("LBOX") },
  { key: "hist", symbol: "HIST", descriptionKey: vehicleMeasureDescription("HIST") },
  { key: "dg", symbol: "DG", descriptionKey: vehicleMeasureDescription("DG") },
];

export const MOCK_VEHICLE_MEASURES: VehicleMeasuresState = {
  wb: { value: "770", calculationValue: "620" },
  cg: { value: "770", calculationValue: "620" },
  fo: { value: "3730", calculationValue: "3730" },
  g: { value: "3970", calculationValue: "3970" },
  k: { value: "59.88%", calculationValue: "59.88%" },
  rg: { value: "5519", calculationValue: "12010" },
  lbox: { value: "5519", calculationValue: "12010" },
  hist: { value: "5519", calculationValue: "12010" },
  dg: { value: "5519", calculationValue: "12010" },
};

export const ADDITIONAL_EQUIPMENT_SECTION_TITLE = additionalEquipment("SECTION_TITLE");
export const ADDITIONAL_EQUIPMENT_COLUMNS = {
  symbol: additionalEquipmentColumn("SYMBOL"),
  description: additionalEquipmentColumn("DESCRIPTION"),
  value: additionalEquipmentColumn("VALUE"),
  calculation: additionalEquipmentColumn("CALCULATION"),
} as const;

export type AdditionalEquipmentConfig = {
  key: AdditionalEquipmentCode;
  symbol: string;
  descriptionKey: string;
};

export const ADDITIONAL_EQUIPMENT_ROWS: AdditionalEquipmentConfig[] = [
  { key: "np1", symbol: "NPr1", descriptionKey: additionalEquipmentDescription("NP1") },
  { key: "np2", symbol: "NP2", descriptionKey: additionalEquipmentDescription("NP2") },
  { key: "wr", symbol: "WR", descriptionKey: additionalEquipmentDescription("WR") },
  { key: "rcg", symbol: "Rcg", descriptionKey: additionalEquipmentDescription("RCG") },
  { key: "wc", symbol: "WC", descriptionKey: additionalEquipmentDescription("WC") },
  { key: "ccg", symbol: "Ccg", descriptionKey: additionalEquipmentDescription("CCG") },
];

export const MOCK_ADDITIONAL_EQUIPMENT: AdditionalEquipmentState = {
  np1: { value: "770", calculationValue: "620" },
  np2: { value: "770", calculationValue: "620" },
  wr: { value: "3730", calculationValue: "3730" },
  rcg: { value: "3970", calculationValue: "3970" },
  wc: { value: "59.88%", calculationValue: "59.88%" },
  ccg: { value: "5519", calculationValue: "12010" },
};

export const FINAL_RESULTS_SECTION_TITLE = finalResults("SECTION_TITLE");
export const FINAL_RESULTS_COLUMNS = {
  parameter: finalResultsColumn("PARAMETER"),
  current: finalResultsColumn("CURRENT_VALUE"),
  allowed: finalResultsColumn("ALLOWED"),
  margin: finalResultsColumn("MARGIN"),
  percent: finalResultsColumn("ALLOWED_PERCENT"),
  status: finalResultsColumn("STATUS"),
} as const;

export type FinalResultRow = {
  parameterKey: string;
  currentValue: string;
  allowedValue: string;
  marginValue: string;
  allowedPercent: string;
  statusKey: string;
};

export const FINAL_RESULTS_ROWS: FinalResultRow[] = [
  {
    parameterKey: finalResultsParameter("CHASSIS_WEIGHT"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("DRIVER_PLUS_PASSENGERS"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("SECOND_ROW_PASSENGERS"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("HYDRAULIC_TAIL_LIFT"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("COOLING_UNIT"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("LOAD"),
    currentValue: "9,438",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.NONE"),
  },
  {
    parameterKey: finalResultsParameter("FRONT_LOAD"),
    currentValue: "6,117",
    allowedValue: "8,000",
    marginValue: "1,883",
    allowedPercent: "76.5%",
    statusKey: finalResults("STATUS.OK"),
  },
  {
    parameterKey: finalResultsParameter("REAR_LOAD"),
    currentValue: "4,698",
    allowedValue: "19,000",
    marginValue: "14,302",
    allowedPercent: "24.7%",
    statusKey: finalResults("STATUS.OK"),
  },
  {
    parameterKey: finalResultsParameter("TOTAL_LOAD"),
    currentValue: "10,815",
    allowedValue: "26,000",
    marginValue: "15,185",
    allowedPercent: "41.6%",
    statusKey: finalResults("STATUS.OK"),
  },
  {
    parameterKey: finalResultsParameter("CENTER_OF_GRAVITY_MM"),
    currentValue: "920",
    allowedValue: "-",
    marginValue: "-",
    allowedPercent: "-",
    statusKey: finalResults("STATUS.OK"),
  },
];

export const QUOTATION_FORM_I18N_PREFIX = "WORK_ORDER.QUOTATION";

const quotationField = (suffix: string) => `${QUOTATION_FORM_I18N_PREFIX}.FIELDS.${suffix}`;
const quotationSection = (suffix: string) => `${QUOTATION_FORM_I18N_PREFIX}.SECTIONS.${suffix}`;
const quotationOption = (suffix: string) => `${QUOTATION_FORM_I18N_PREFIX}.OPTIONS.${suffix}`;

const qCard = (
  enabled = true,
  selectedOption = "",
  note = "",
  quantity = "",
  size = "",
  location = "",
  optionEnabled = false
) => ({
  enabled,
  selectedOption,
  note,
  quantity,
  size,
  location,
  itemType: "",
  optionEnabled,
});

export const MOCK_QUOTATION_BOX_STRUCTURE = {
  frontWall: {
    enabled: true,
    selectedOption: "LOW_TIN_PIPES",
    height: "1400",
    treadSurface: true,
    treadNote: "",
    widthReinforcement: true,
    widthNote: "",
  },
  backWall: qCard(true, "LOW_TIN_PIPES", "", "", "1400", "", true),
  frontRack: qCard(true),
  backRack: qCard(true),
  sideDoors: qCard(true, "", "", "400"),
  pillars: qCard(true, "GAS_MODEL", "", "99"),
  hydraulicSide: qCard(true, "STANDARD"),
  rearDoor: qCard(true),
};

export const MOCK_QUOTATION_ROOF_BOX_STRUCTURE = {
  roofType: qCard(true, "FIBERGLASS"),
  hydraulicRoof: qCard(true),
  detachableRoof: qCard(true),
  longitudinalBeams: qCard(true, "SHEET_200_3"),
  middlePillar: qCard(true),
  frontWall: qCard(true, "REGULAR_FRONT_WALL"),
  rearFrame: qCard(true, "REGULAR"),
  rearOpening: qCard(true, "OPEN"),
  hydraulicSide: qCard(true),
  sideCompletionFlap: qCard(true),
  sideDoors: qCard(true, "TIN_DOOR", "", "1", "400"),
  sidePillars: qCard(true),
  plywoodInsteadOfCurtain: qCard(true),
  curtainTypeAndTensioners: qCard(true),
  tensionType: qCard(true),
  curtainRail: qCard(true),
  curtainEdge: qCard(true),
};

export const MOCK_QUOTATION_CLOSED_BOX_STRUCTURE = {
  roofType: qCard(true, "FIBERGLASS"),
  frontWall: qCard(true, "REGULAR_FRONT_WALL"),
  rearFrame: qCard(true, "REGULAR"),
  frontWallReinforcement: qCard(true),
  sideDoorOpenings: qCard(
    true,
    "",
    "QUOTES.DETAILS.FORM.CLOSED_BOX.MOCK.SIDE_DOOR_LOCATION",
    "1"
  ),
  rearOpening: qCard(true, "OPEN"),
  hydraulicSide: qCard(true, "TON_1"),
  sideCompletionFlap: qCard(true, "REGULAR_GAS_PISTONS"),
};

const insCard = (
  selectedOption = "",
  extra: { note?: string; quantity?: string; size?: string; length?: string } = {}
) => ({
  ...qCard(
    true,
    selectedOption,
    extra.note ?? "",
    extra.quantity ?? "",
    extra.size ?? ""
  ),
  length: extra.length ?? "",
});

export const MOCK_QUOTATION_INSULATED_BOX_STRUCTURE = {
  roofType: insCard("INSULATED_40_FIBER"),
  frontWall: insCard("", { size: "50" }),
  rearFrame: insCard("REGULAR"),
  frontWallReinforcement: insCard(),
  sideDoorOpenings: insCard("", {
    note: "QUOTES.DETAILS.FORM.INSULATED_BOX.MOCK.SIDE_DOOR_LOCATION",
    quantity: "1",
    size: "50",
    length: "50",
  }),
  rearOpening: insCard("OPEN"),
  hydraulicSide: insCard("TON_1"),
  sideCompletionFlap: insCard("REGULAR_GAS_PISTONS"),
};

const accCard = (
  selectedOption = "",
  quantity = "",
  extra: { size?: string; length?: string; width?: string; height?: string } = {}
) => ({
  ...qCard(true, selectedOption, "", quantity, extra.size ?? ""),
  length: extra.length ?? "",
  width: extra.width ?? "",
  height: extra.height ?? "",
});

export const MOCK_QUOTATION_ROOF_BOX_ACCESSORIES = {
  sideCableTensioner: accCard("WIDE", "0"),
  strapTensioners: accCard("WIDE", "0"),
  frontCableTensioner: accCard(),
  recessedRings: accCard(),
  apcRings: accCard(),
  additionalTyingHooks: accCard(),
  toolbox: accCard("SILAGI_SHEET", "1", { size: "1200X600X600" }),
  openBasket: accCard("", "1", { length: "99", width: "99", height: "99" }),
  polesBasket: accCard("", "1"),
  palletJackMount: accCard(),
  polesStorageMount: accCard(),
  jerrycanMount: accCard(),
  rearBumper: accCard(),
  hazmatSigns: accCard(),
  spareWheelCarrier: accCard(),
  rearLightProtectionFrame: accCard(),
  extinguisherMount: accCard(),
  wings: accCard(),
  roundIronSides: accCard(),
  dieselLock: accCard(),
  ureaLock: accCard(),
  batteriesLock: accCard(),
  rearLightMounts: accCard(),
  sideLighting: accCard(),
  microphoneLights: accCard(),
  interiorLighting: accCard(),
  projectors: accCard(),
};

export const MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES = {
  recessedRings: {
    ...accCard("", "0"),
    note: "QUOTES.DETAILS.FORM.CLOSED_BOX.MOCK.RECESSED_RINGS_NOTE",
  },
  apcRings: {
    ...accCard("TYPE_A", "0"),
    note: "QUOTES.DETAILS.FORM.CLOSED_BOX.MOCK.APC_RINGS_NOTE",
  },
  toolbox: accCard("SILAGI_SHEET", "1", { size: "1200X600X600" }),
  palletJackMount: accCard("", "1"),
  openBasket: accCard("", "1", { length: "99", width: "99", height: "99" }),
  jerrycan: accCard("", "1"),
  rearBumper: accCard("REGULAR"),
  spareWheelCarrier: accCard("LOWER"),
  extinguisherMount: accCard("", "1"),
  hazmatSigns: accCard(),
  rearLightMounts: accCard(),
  lashingStrips: accCard(),
  wings: accCard(),
  hydraulicControlBoxLock: accCard(),
  ureaLock: accCard(),
  dieselLock: accCard(),
  sideDoorLock: accCard(),
  hydraulicSideLockEar: accCard(),
  batteriesLock: accCard(),
  sideGuardsReflectors: accCard(),
  sideLighting: accCard(),
  standardMudguards: accCard(),
  tyingStrips: [
    {
      id: "closed-tying-strip-1",
      location: "QUOTES.DETAILS.FORM.CLOSED_BOX.OPTIONS.FRONT",
      itemType: "",
      quantity: "",
      height: "QUOTES.DETAILS.FORM.CLOSED_BOX.OPTIONS.HEIGHT_UNIT_MM",
      note: "QUOTES.DETAILS.FORM.CLOSED_BOX.FIELDS.NOTE",
    },
  ],
};

export const MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES = {
  recessedRings: {
    ...accCard("", "0"),
    note: "QUOTES.DETAILS.FORM.INSULATED_BOX.MOCK.RECESSED_RINGS_NOTE",
  },
  apcRings: {
    ...accCard("TYPE_A", "0"),
    note: "QUOTES.DETAILS.FORM.INSULATED_BOX.MOCK.APC_RINGS_NOTE",
  },
  toolbox: accCard("SILAGI_SHEET", "1", { size: "1200X600X600" }),
  openBasket: accCard("", "1", { length: "99", width: "99", height: "99" }),
  palletJackMount: accCard("", "1"),
  jerrycan: accCard(),
  rearBumper: accCard("REGULAR"),
  rearLightMounts: accCard(),
  hazmatSigns: accCard(),
  extinguisherMount: accCard(),
  spareWheelCarrier: accCard(),
  lashingStrips: accCard(),
  wings: accCard(),
  coolingUnit: accCard("THERMO_KING"),
  hydraulicControlBoxLock: accCard(),
  ureaLock: accCard(),
  dieselLock: accCard(),
  sideDoorLock: accCard(),
  hydraulicSideLockEar: accCard(),
  batteriesLock: accCard(),
  sideGuardsReflectors: accCard(),
  sideLighting: accCard(),
  standardMudguards: accCard(),
  tyingStrips: [
    {
      id: "insulated-tying-strip-1",
      location: "QUOTES.DETAILS.FORM.INSULATED_BOX.OPTIONS.FRONT",
      itemType: "",
      quantity: "",
      height: "QUOTES.DETAILS.FORM.INSULATED_BOX.OPTIONS.HEIGHT_UNIT_MM",
      note: "QUOTES.DETAILS.FORM.INSULATED_BOX.FIELDS.NOTE",
    },
  ],
};

const tieItem = (quantity = "99", selectedOption = "") => qCard(true, selectedOption, "", quantity);
const tyingTableValue = (suffix: string) =>
  `${QUOTATION_FORM_I18N_PREFIX}.TYING_EQUIPMENT.TABLE_VALUES.${suffix}`;
const quotationTableRow = (
  id: string,
  itemType: string,
  quantity: string,
  note = "-",
  height = ""
) => ({
  id,
  location: "",
  itemType,
  quantity,
  note,
  height,
});

export const MOCK_QUOTATION_TYING_EQUIPMENT = {
  sideCableTensioner: tieItem("99"),
  strapTensioners: tieItem("99", "WIDE"),
  frontCableTensioner: tieItem("99"),
  recessedRings: tieItem("99"),
  apcRings: tieItem("99"),
  additionalTyingHooks: tieItem("99"),
  toolboxes: [
    quotationTableRow("toolbox-1", tyingTableValue("TOOLBOX_TYPE"), "1", "-", "1200X600X600"),
  ],
  openBasket: [
    quotationTableRow("open-basket-1", tyingTableValue("GAS_MODEL"), "99"),
  ],
  polesBasket: tieItem("1"),
  waterJerrycan: tieItem("1"),
  palletJackMount: qCard(true, "FROM_SIDE"),
};

export type QuotationEquipmentTableColumnKey = "itemType" | "height" | "quantity" | "note";

const tyingEquipmentKey = (suffix: string) =>
  `${QUOTATION_FORM_I18N_PREFIX}.TYING_EQUIPMENT.${suffix}`;
const tyingTableColumn = (suffix: string) => tyingEquipmentKey(`TABLE_COLUMNS.${suffix}`);

export const QUOTATION_EQUIPMENT_TABLE_COLUMN_LABELS: Record<
  QuotationEquipmentTableColumnKey,
  string
> = {
  itemType: tyingTableColumn("TYPE"),
  height: tyingTableColumn("SIZE"),
  quantity: tyingTableColumn("QUANTITY"),
  note: tyingTableColumn("NOTE"),
};

export const QUOTATION_EQUIPMENT_TABLES = [
  {
    tableKey: "toolboxes" as const,
    titleKey: tyingEquipmentKey("TABLES.TOOLBOXES"),
    columns: ["itemType", "height", "quantity", "note"] as QuotationEquipmentTableColumnKey[],
    variant: "toolbox" as const,
  },
  {
    tableKey: "openBasket" as const,
    titleKey: tyingEquipmentKey("TABLES.OPEN_BASKET"),
    columns: ["itemType", "quantity", "note"] as QuotationEquipmentTableColumnKey[],
    variant: "openBasket" as const,
  },
];

export const MOCK_QUOTATION_SAFETY_VEHICLE = {
  spareWheelCarrier: qCard(true, "REGULAR"),
  hazardousMaterialsMounts: qCard(true),
  rearBumper: qCard(true),
  couplingLocks: qCard(true),
  extinguisherMounts: qCard(true),
  rearLightMounts: qCard(true, "", "", "", "", "", true),
  wings: { ...qCard(true, "", "", "2"), itemType: "TIN", location: "FRONT" },
  roundIronSides: qCard(true, "", "", "", "10"),
};

export const MOCK_QUOTATION_LOCKS = {
  dieselLock: qCard(true),
  ureaLock: qCard(true),
  batteriesLock: qCard(true),
  hydraulicTailLiftMotorLock: qCard(true),
  hydraulicTailLiftLock: qCard(true, "HASP"),
  spareWheelLock: qCard(true),
};

export const MOCK_QUOTATION_STANDARD_95 = {
  sideGuardsReflectors: qCard(true, "ALUMINUM"),
  sideLighting: qCard(true),
  standardMudguards: qCard(true),
};

export const MOCK_QUOTATION_FORM_VALUES = {
  totalSale: "0",
  profitAmount: "0",
  profitPercent: "0",
  theoreticalPrice: "0",
  totalCost: "0",
  totalMaterialCost: "0",
  workHours: "0",
  perHour: "90",
  totalWorkCost: "0",
  repairHours: "0",
  sheetMetalCost: "0",
  craneCompletionAddition: "90",
  exchangeRate: "0",
  otherSaleItem: "0",
  variableCoefficient: "1.4",
  downPayment: "0",
  vehicleWeightWithoutSide: "0",
  assembledLength: "12000",
  assembledWidth: "12000",
  assembledHeight: "2550",
  floorBase: "U65",
  floorType: "BLACK_SHEET",
  floorThickness: "4",
  crossBeams: "OMEGA_3MM",
  reinforcedModel: false,
  dollarRate: "3.85",
  estimatedWeight: "0",
  calculated: "0",
  coefficients: "0",
  curtainPart: "0",
  curtainAndAdvertising: "0",
  curtainWithoutBarrier: "0",
  advertisingWithoutCurtain: "0",
  withoutCurtainWithoutAdvertising: "0",
  leasingInstallation: "0",
  bodyHeightRange: "ABOVE_2_44",
  adjustedHeight: "MM",
};

export const QUOTATION_SUMMARY_FIELDS: QuotationFieldConfig[] = [
  { field: "totalSale", labelKey: quotationField("TOTAL_SALE"), readOnly: true, currency: true },
  { field: "theoreticalPrice", labelKey: quotationField("THEORETICAL_PRICE"), readOnly: true, currency: true },
  { field: "totalCost", labelKey: quotationField("TOTAL_COST"), readOnly: true, currency: true },
  { field: "totalMaterialCost", labelKey: quotationField("TOTAL_MATERIAL_COST"), readOnly: true, currency: true },
];

export const QUOTATION_BASE_COSTS_FIELDS: QuotationFieldConfig[] = [
  { field: "workHours", labelKey: quotationField("WORK_HOURS"), type: "number" },
  { field: "perHour", labelKey: quotationField("PER_HOUR"), type: "number" },
  { field: "totalWorkCost", labelKey: quotationField("TOTAL_WORK_COST"), readOnly: true, currency: true },
  { field: "repairHours", labelKey: quotationField("REPAIR_HOURS"), type: "number" },
];

export const QUOTATION_PARAMETERS_FIELDS: QuotationFieldConfig[] = [
  { field: "sheetMetalCost", labelKey: quotationField("SHEET_METAL_COST"), type: "number" },
  { field: "craneCompletionAddition", labelKey: quotationField("CRANE_COMPLETION_ADDITION"), type: "number" },
  { field: "exchangeRate", labelKey: quotationField("EXCHANGE_RATE"), readOnly: true, currency: true },
  { field: "otherSaleItem", labelKey: quotationField("OTHER_SALE_ITEM"), type: "number" },
];

export const QUOTATION_PRICING_FIELDS: QuotationFieldConfig[] = [
  { field: "variableCoefficient", labelKey: quotationField("VARIABLE_COEFFICIENT"), type: "number" },
  { field: "downPayment", labelKey: quotationField("DOWN_PAYMENT"), type: "number" },
  { field: "vehicleWeightWithoutSide", labelKey: quotationField("VEHICLE_WEIGHT_WITHOUT_SIDE"), type: "number" },
];

export const QUOTATION_SECTION_KEYS = {
  BASE_COSTS: quotationSection("BASE_COSTS"),
  PARAMETERS: quotationSection("PARAMETERS"),
  PRICING: quotationSection("PRICING"),
  BOX_DIMENSIONS: quotationSection("BOX_DIMENSIONS"),
  FLOOR: quotationSection("FLOOR"),
  BOX_STRUCTURE: quotationSection("BOX_STRUCTURE"),
  EQUIPMENT: quotationSection("EQUIPMENT"),
  STANDARD_95: quotationSection("STANDARD_95"),
} as const;

export const QUOTATION_BOX_DIMENSIONS_FIELDS: FormFieldConfig<QuotationFormValues>[] = [
  {
    field: "assembledLength",
    labelKey: quotationField("ASSEMBLED_LENGTH"),
    type: "number",
    showSpinners: true,
  },
  {
    field: "assembledWidth",
    labelKey: quotationField("ASSEMBLED_WIDTH"),
    type: "number",
    showSpinners: true,
  },
];

export const QUOTATION_FLOOR_FIELDS: FormFieldConfig<QuotationFormValues>[] = [
  {
    field: "floorBase",
    labelKey: quotationField("FLOOR_BASE"),
    type: "select",
    options: [["Z220", quotationOption("FLOOR_BASE_Z220")]],
  },
  {
    field: "floorType",
    labelKey: quotationField("FLOOR_TYPE"),
    type: "select",
    options: [["BLACK_SHEET", quotationOption("FLOOR_TYPE_BLACK_SHEET")]],
  },
  {
    field: "floorThickness",
    labelKey: quotationField("FLOOR_THICKNESS"),
    type: "number",
    showSpinners: true,
  },
  {
    field: "crossBeams",
    labelKey: quotationField("CROSS_BEAMS"),
    type: "select",
  },
];
